import { isEqual } from "lodash"
import type { NextRouter } from "next/router"
import React from "react"

import { Appointments } from "@/lib/appointments"
import { buildAppointmentOfferings } from "@/lib/appointments/buildOfferings"
import { buildAppointmentsPerformResults } from "@/lib/appointments/buildPerformResults"
import { buildAppointmentsCacheKeys } from "@/lib/appointments/cacheKeys/build"
import { AppointmentsContext } from "@/lib/appointments/context"
import { emptyAppointmentOfferingsPayload } from "@/lib/appointments/emptyPayload"
import type { BookingSearch } from "@/lib/bookingSearch"
import type { Caches } from "@/lib/caches"
import type { Caching } from "@/lib/caching"
import { Loading } from "@/lib/loading"
import { Modelling } from "@/lib/models"
import type { Notices } from "@/lib/notices"
import { ReactPerformance } from "@/lib/react_performance"
import { debugEnabled } from "@/lib/routing/debugEnabled"
import { getQueryParam } from "@/lib/routing/getQueryParam"
import type { Services } from "@/lib/services"

export type AppointmentsContainerProps = {
  bookingSearch: BookingSearch.ContextData
  caching: Caches.ContextData
  children: React.ReactNode
  loading: Loading.ContextData
  noticeFunctions: Notices.ContextFunctions
  router: NextRouter
  services: Services.ContextData
}

type State = {
  loadingConfig: Loading.Config
  processing: string | undefined
  results: Appointments.PerformResults
  update: boolean
}

export class ApppointmentsContainerComponent extends React.Component<
  AppointmentsContainerProps,
  State
> {
  public state: State = {
    loadingConfig: {
      dependencies: [
        `bookingSearch`,
        `caching`,
        `notices`,
        `routing`,
        `services`,
      ],
      processId: `appointments`,
    },
    processing: undefined,
    results: buildAppointmentsPerformResults(),
    update: false,
  }

  public componponentDidMount = async () => {
    this.componentMaybeReady()
  }

  public componentDidUpdate = async () => {
    const loading = await this.componentMaybeReady()

    if (loading) {
      return
    }

    if (this.cacheKeysChanged(this.props, this.props)) {
      await this.perform()
    }
  }

  private componentMaybeReady = Loading.curryOnMaybeReady(this, {})

  private getCacheKeys = (
    props: AppointmentsContainerProps
  ): Appointments.OfferingsCache.Keys | undefined => {
    const keyData = this.getCacheKeyData(props)
    if (!keyData) {
      return
    }

    return buildAppointmentsCacheKeys(keyData)
  }

  private getCacheKeyData = (
    props: AppointmentsContainerProps
  ): Appointments.OfferingsCache.KeyData | undefined => {
    if (!props.bookingSearch.hasAddress) {
      return
    }

    const practiceId = getQueryParam({
      router: props.router,
      key: `practice-id`,
      warnOnFailure: false,
    })

    const startTime = getQueryParam({
      router: props.router,
      key: `timeslot`,
      warnOnFailure: false,
    })

    if (!practiceId || !startTime) {
      return
    }

    return {
      practiceId,
      service: props.services.selected,
      startTime,
    }
  }

  private cacheKeysChanged = (
    first: AppointmentsContainerProps,
    second: AppointmentsContainerProps
  ) => {
    return !isEqual(this.getCacheKeys(first), this.getCacheKeys(second))
  }

  private onAppointmentOfferingsValidationFailure: Caching.Functions.OnValidationFailure<
    Caches.Store,
    Appointments.OfferingsCache.Keys,
    Appointments.OfferingsCache.Data
  > = ({ cacheArgs, result }) => {
    if (debugEnabled(this.props.router)) {
      const count =
        ((result.subject as Appointments.OfferingsCache.Data)?.array?.length ||
          0) - (result.salvage?.array?.length || 0)

      const resultTerm = count === 1 ? `offerings` : `offering`

      this.props.noticeFunctions.displayNotice({
        id: `appointmentOfferingsValidationFailure`,
        message: `${count} appointment ${resultTerm} failed validation; see console for details`,
        type: `error`,
      })

      console.log(`Appointment ${resultTerm} failed validation`, {
        cacheArgs,
        failures: result.failures,
        reasons: result.failures.map((failure) => {
          return failure.reason
        }),
        result,
      })
    }
  }

  private perform = async () => {
    this.updateState({ processing: `perform` })

    const keyData = this.getCacheKeyData(this.props)
    const keys = this.getCacheKeys(this.props)

    if (!keys || !keyData) {
      this.updateState({ processing: undefined })

      return buildAppointmentsPerformResults()
    }

    const { practiceId, startTime } = keyData

    const { businesses } = await this.props.bookingSearch.perform()

    if (!businesses.data.collection[practiceId]) {
      this.updateState({ processing: undefined })

      return buildAppointmentsPerformResults()
    }

    const appointmentOfferings =
      (await this.props.caching.cache<
        Appointments.OfferingsCache.Keys,
        Appointments.OfferingsCache.Data
      >({
        allowSalvage: true,
        caller: `AppointmentsContainer#perform`,
        fetch: async () => {
          this.updateState((state) => {
            return {
              ...state,
              processing: `appointmentOfferings`,
            }
          })

          return buildAppointmentOfferings({
            businesses: businesses.data,
            practiceId,
            startTime,
          })
        },
        fetchName: `buildAppointmentOfferings`,
        keys,
        metadata: {
          expiry:
            this.props.bookingSearch.results.businesses.caching.expiry ||
            Appointments.OfferingsCache.expiry,
        },
        onValidationFailure: this.onAppointmentOfferingsValidationFailure,
        validator: Modelling.validator(`AppointmentOffering`, `mirror`),
      })) || emptyAppointmentOfferingsPayload

    const results = {
      appointmentOfferings,
    }

    this.updateState((state) => {
      return {
        ...state,
        processing: undefined,
        results,
      }
    })

    return results
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    AppointmentsContainerProps,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps, nextState }) => {
      if (Loading.shouldComponentReady(nextProps, nextState)) {
        return { update: true }
      }

      if (
        Loading.processIsReady(nextProps, `appointments`) &&
        this.cacheKeysChanged(this.props, nextProps)
      ) {
        return { update: true }
      }

      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private updateState = ReactPerformance.curryUpdateManagedState(this)

  public render = () => {
    return (
      <AppointmentsContext.Provider
        value={{
          results: this.state.results,
          perform: this.perform,
          processing: this.state.processing,
        }}
      >
        {this.props.children}
      </AppointmentsContext.Provider>
    )
  }
}
